import { useState, useEffect, useCallback } from 'react';
import { getDocuments, updateDocument } from '../services/api';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, FileText, Download, ChevronDown, X, Calendar, Building2, Folder, FolderOpen, ChevronRight, File, Archive, MoreVertical, FolderInput } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import Sidebar from '../components/layout/Sidebar';
import DocumentDrawer from '../components/DocumentDrawer';
import JSZip from 'jszip';

const ArchivePage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(['Rechnungen', 'Mahnungen']);
  const [viewMode, setViewMode] = useState('folders'); // 'folders' or 'list'
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMonth, setExportMonth] = useState('');
  const [exportType, setExportType] = useState('all'); // 'all' or specific folder type
  const [exporting, setExporting] = useState(false);

  // Generate last 12 months for export selection
  const exportMonthOptions = eachMonthOfInterval({
    start: subMonths(new Date(), 11),
    end: new Date()
  }).reverse().map(date => ({
    value: format(date, 'yyyy-MM'),
    label: format(date, 'MMMM yyyy', { locale: de })
  }));

  const loadDocuments = useCallback(async () => {
    try {
      const params = {};
      if (docTypeFilter !== 'all') params.doc_type = docTypeFilter;
      
      const response = await getDocuments(params);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [docTypeFilter]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.sender_name?.toLowerCase().includes(query) ||
      doc.summary?.toLowerCase().includes(query) ||
      doc.invoice_no?.toLowerCase().includes(query) ||
      doc.reference?.toLowerCase().includes(query)
    );
  });

  const docTypes = [
    'Banken', 
    'Telekommunikation', 
    'Versicherungen', 
    'Energie', 
    'Miete', 
    'Behörden', 
    'Bußgelder', 
    'Verträge', 
    'Gesundheit', 
    'Abonnements', 
    'Sonstiges'
  ];

  const totalAmount = filteredDocuments.reduce((sum, d) => sum + (d.amount || 0), 0);

  // Move document to folder
  const moveDocumentToFolder = async (docId, newType) => {
    try {
      await updateDocument(docId, { doc_type: newType });
      toast.success('Dokument verschoben');
      loadDocuments();
    } catch (error) {
      toast.error('Fehler beim Verschieben');
    }
  };

  // Download folder as ZIP (placeholder - would need backend implementation)
  const downloadFolder = (folderName, folderType) => {
    const docs = getDocsByFolder(folderType);
    if (docs.length === 0) {
      toast.error('Ordner ist leer');
      return;
    }
    toast.info(`${docs.length} Dokumente aus "${folderName}" werden heruntergeladen...`);
    // TODO: Implement actual ZIP download via backend
  };

  // Group documents by type for folder view
  const folderStructure = [
    { name: 'Banken & Finanzen', type: 'Banken', icon: '🏦', color: 'indigo' },
    { name: 'Telekommunikation', type: 'Telekommunikation', icon: '📱', color: 'pink' },
    { name: 'Versicherungen', type: 'Versicherungen', icon: '🛡️', color: 'cyan' },
    { name: 'Strom & Gas', type: 'Energie', icon: '⚡', color: 'yellow' },
    { name: 'Miete & Nebenkosten', type: 'Miete', icon: '🏠', color: 'emerald' },
    { name: 'Behörden & Ämter', type: 'Behörden', icon: '🏛️', color: 'violet' },
    { name: 'Bußgelder & Blitzer', type: 'Bußgelder', icon: '🚗', color: 'rose' },
    { name: 'Verträge', type: 'Verträge', icon: '📝', color: 'orange' },
    { name: 'Gesundheit & Krankenkasse', type: 'Gesundheit', icon: '🏥', color: 'red' },
    { name: 'Abonnements', type: 'Abonnements', icon: '📺', color: 'purple' },
    { name: 'Sonstiges', type: 'Sonstiges', icon: '📁', color: 'gray' },
  ];

  const getDocsByFolder = (type) => filteredDocuments.filter(d => d.doc_type === type);

  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => 
      prev.includes(folderName) 
        ? prev.filter(f => f !== folderName)
        : [...prev, folderName]
    );
  };

  const handleExport = async () => {
    if (!exportMonth) {
      toast.error('Bitte einen Monat auswählen');
      return;
    }

    setExporting(true);
    try {
      const zip = new JSZip();
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      // Filter documents by month and optionally by type
      const docsToExport = documents.filter(doc => {
        const docMonth = doc.document_month || doc.created_at?.substring(0, 7);
        const monthMatch = docMonth === exportMonth;
        const typeMatch = exportType === 'all' || doc.doc_type === exportType;
        return monthMatch && typeMatch;
      });

      if (docsToExport.length === 0) {
        toast.error('Keine Dokumente für diesen Zeitraum gefunden');
        setExporting(false);
        return;
      }

      // Group documents by type
      const groupedDocs = {};
      docsToExport.forEach(doc => {
        const type = doc.doc_type || 'Sonstiges';
        if (!groupedDocs[type]) groupedDocs[type] = [];
        groupedDocs[type].push(doc);
      });

      // Download and add each document to zip
      for (const [type, docs] of Object.entries(groupedDocs)) {
        const folder = zip.folder(type);
        for (const doc of docs) {
          if (doc.file_name) {
            try {
              const response = await fetch(`${API_URL}/api/documents/${doc.id}/file`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const blob = await response.blob();
                const fileName = doc.original_name || doc.file_name;
                folder.file(fileName, blob);
              }
            } catch (e) {
              console.error(`Error downloading ${doc.id}:`, e);
            }
          }
        }
      }

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dokumente_${exportMonth}${exportType !== 'all' ? `_${exportType}` : ''}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${docsToExport.length} Dokumente exportiert`);
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Exportieren');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${document.documentElement.classList.contains('dark') ? 'galaxy-bg' : 'bg-background'}`} data-testid="archive-page">
      {/* Light Mode Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10 dark:bg-white/5 dark:border-white/10">
            <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <Archive className="w-6 h-6 text-violet-500" />
                <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Archiv
                </h1>
              </div>

              <div className="flex-1 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Dokumente durchsuchen..."
                    className="pl-10 h-10 rounded-xl glass-input dark:bg-white/10 dark:border-white/20"
                    data-testid="archive-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                      <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>

                {/* View Toggle */}
                <div className="flex glass-card rounded-xl p-1 dark:bg-white/5">
                  <button
                    onClick={() => setViewMode('folders')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === 'folders' 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Folder className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === 'list' 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                </div>

                {/* Export Dialog */}
                <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/25">
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">Export</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900/95 border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Dokumente exportieren
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-gray-300">Monat auswählen *</Label>
                        <Select value={exportMonth} onValueChange={setExportMonth}>
                          <SelectTrigger className="mt-2 bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Monat wählen..." />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-white/10">
                            {exportMonthOptions.map(option => (
                              <SelectItem key={option.value} value={option.value} className="text-white hover:bg-white/10">
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Kategorie</Label>
                        <Select value={exportType} onValueChange={setExportType}>
                          <SelectTrigger className="mt-2 bg-white/10 border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-white/10">
                            <SelectItem value="all" className="text-white hover:bg-white/10">Alle Kategorien</SelectItem>
                            {folderStructure.map(folder => (
                              <SelectItem key={folder.type} value={folder.type} className="text-white hover:bg-white/10">
                                {folder.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        onClick={handleExport}
                        disabled={exporting || !exportMonth}
                        className="w-full bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl shadow-lg shadow-violet-500/25"
                      >
                        {exporting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />
                            Exportiere...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Als ZIP exportieren
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </header>

          {/* Stats */}
          <div className="px-6 py-4 flex gap-4 overflow-x-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl px-6 py-4 flex-shrink-0 dark:bg-white/5"
            >
              <p className="text-sm text-muted-foreground">Dokumente</p>
              <p className="text-2xl font-bold text-foreground">{filteredDocuments.length}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.1 }}
              className="glass-card rounded-2xl px-6 py-4 flex-shrink-0 dark:bg-white/5"
            >
              <p className="text-sm text-muted-foreground">Gesamtbetrag</p>
              <p className="text-2xl font-bold tabular-nums text-foreground">{formatCurrency(totalAmount)}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.2 }}
              className="glass-card rounded-2xl px-6 py-4 flex-shrink-0 dark:bg-white/5"
            >
              <p className="text-sm text-muted-foreground">Ordner</p>
              <p className="text-2xl font-bold text-foreground">{folderStructure.length}</p>
            </motion.div>
          </div>

          {/* Folder View */}
          {viewMode === 'folders' ? (
            <div className="px-6 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {folderStructure.map((folder, index) => {
                  const docs = getDocsByFolder(folder.type);
                  const isExpanded = expandedFolders.includes(folder.name);
                  const folderTotal = docs.reduce((sum, d) => sum + (d.amount || 0), 0);
                  
                  return (
                    <motion.div
                      key={folder.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="glass-card rounded-2xl overflow-hidden dark:bg-white/5 dark:border-white/10"
                    >
                      {/* Folder Header */}
                      <button
                        onClick={() => toggleFolder(folder.name)}
                        className={`w-full px-4 py-4 flex items-center justify-between hover:bg-white/20 dark:hover:bg-white/10 transition-colors ${
                          folder.color === 'blue' ? 'border-l-4 border-blue-500' :
                          folder.color === 'red' ? 'border-l-4 border-red-500' :
                          folder.color === 'emerald' ? 'border-l-4 border-emerald-500' :
                          folder.color === 'violet' ? 'border-l-4 border-violet-500' :
                          folder.color === 'orange' ? 'border-l-4 border-orange-500' :
                          folder.color === 'yellow' ? 'border-l-4 border-yellow-500' :
                          folder.color === 'cyan' ? 'border-l-4 border-cyan-500' :
                          folder.color === 'pink' ? 'border-l-4 border-pink-500' :
                          folder.color === 'indigo' ? 'border-l-4 border-indigo-500' :
                          folder.color === 'teal' ? 'border-l-4 border-teal-500' :
                          folder.color === 'rose' ? 'border-l-4 border-rose-500' :
                          'border-l-4 border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <FolderOpen className={`w-6 h-6 ${
                              folder.color === 'blue' ? 'text-blue-500' :
                              folder.color === 'red' ? 'text-red-500' :
                              folder.color === 'emerald' ? 'text-emerald-500' :
                              folder.color === 'violet' ? 'text-violet-500' :
                              folder.color === 'orange' ? 'text-orange-500' :
                              folder.color === 'yellow' ? 'text-yellow-500' :
                              folder.color === 'cyan' ? 'text-cyan-500' :
                              folder.color === 'pink' ? 'text-pink-500' :
                              folder.color === 'indigo' ? 'text-indigo-500' :
                              folder.color === 'teal' ? 'text-teal-500' :
                              folder.color === 'rose' ? 'text-rose-500' :
                              'text-gray-500'
                            }`} />
                          ) : (
                            <Folder className={`w-6 h-6 ${
                              folder.color === 'blue' ? 'text-blue-500' :
                              folder.color === 'red' ? 'text-red-500' :
                              folder.color === 'emerald' ? 'text-emerald-500' :
                              folder.color === 'violet' ? 'text-violet-500' :
                              folder.color === 'orange' ? 'text-orange-500' :
                              folder.color === 'yellow' ? 'text-yellow-500' :
                              folder.color === 'cyan' ? 'text-cyan-500' :
                              folder.color === 'pink' ? 'text-pink-500' :
                              folder.color === 'indigo' ? 'text-indigo-500' :
                              folder.color === 'teal' ? 'text-teal-500' :
                              folder.color === 'rose' ? 'text-rose-500' :
                              'text-gray-500'
                            }`} />
                          )}
                          <div className="text-left">
                            <p className="font-semibold text-foreground">{folder.name}</p>
                            <p className="text-xs text-muted-foreground">{docs.length} Dokumente · {formatCurrency(folderTotal)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          {docs.length > 0 && (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); downloadFolder(folder.name, folder.type); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); downloadFolder(folder.name, folder.type); }}}
                              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
                              title="Ordner herunterladen"
                            >
                              <Download className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            </div>
                          )}
                        </div>
                      </button>
                      
                      {/* Folder Contents */}
                      <AnimatePresence>
                        {isExpanded && docs.length > 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-white/10 dark:border-white/5"
                          >
                            <div className="max-h-[300px] overflow-y-auto">
                              {docs.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="px-4 py-3 flex items-center gap-3 hover:bg-white/20 dark:hover:bg-white/10 border-b border-white/5 last:border-0 group"
                                >
                                  <div 
                                    onClick={() => { setSelectedDoc(doc); setDrawerOpen(true); }}
                                    className="flex items-center gap-3 flex-1 cursor-pointer min-w-0"
                                  >
                                    <File className="w-4 h-4 text-muted-foreground" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-foreground truncate">{doc.sender_name || 'Unbekannt'}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {doc.created_at && format(new Date(doc.created_at), 'd. MMM yyyy', { locale: de })}
                                      </p>
                                    </div>
                                    <span className="text-sm font-semibold text-foreground tabular-nums">{formatCurrency(doc.amount)}</span>
                                  </div>
                                  
                                  {/* Move to folder dropdown */}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/20 rounded-lg transition-all">
                                        <MoreVertical className="w-4 h-4 text-muted-foreground" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                      <DropdownMenuLabel className="flex items-center gap-2 text-xs">
                                        <FolderInput className="w-3 h-3" />
                                        In Ordner verschieben
                                      </DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      {folderStructure.filter(f => f.type !== folder.type).map((targetFolder) => (
                                        <DropdownMenuItem 
                                          key={targetFolder.type}
                                          onClick={() => moveDocumentToFolder(doc.id, targetFolder.type)}
                                          className="gap-2"
                                        >
                                          <Folder className={`w-4 h-4 ${
                                            targetFolder.color === 'blue' ? 'text-blue-500' :
                                            targetFolder.color === 'red' ? 'text-red-500' :
                                            targetFolder.color === 'emerald' ? 'text-emerald-500' :
                                            targetFolder.color === 'violet' ? 'text-violet-500' :
                                            'text-gray-500'
                                          }`} />
                                          {targetFolder.name}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                        {isExpanded && docs.length === 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-4 py-6 text-center text-muted-foreground border-t border-white/10"
                          >
                            <p className="text-sm">Keine Dokumente in diesem Ordner</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ) : (
          /* List View */
          <div className="px-6 pb-8">
            <div className="glass-card rounded-2xl overflow-hidden dark:bg-white/5 dark:border-white/10">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-white/30 dark:bg-white/5 text-sm font-medium text-muted-foreground border-b border-white/10 dark:border-white/5">
                <div className="col-span-4">Dokument</div>
                <div className="col-span-2">Typ</div>
                <div className="col-span-2">Betrag</div>
                <div className="col-span-2">Datum</div>
                <div className="col-span-2">Status</div>
              </div>

              {/* Table Body */}
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-16">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-muted-foreground">Keine Dokumente gefunden</p>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-primary hover:underline mt-2"
                    >
                      Suche zurücksetzen
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-white/5 dark:divide-white/5">
                  {filteredDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => { setSelectedDoc(doc); setDrawerOpen(true); }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-white/20 dark:hover:bg-white/5 cursor-pointer transition-colors items-center"
                      data-testid={`archive-doc-${doc.id}`}
                    >
                      {/* Document Info */}
                      <div className="col-span-12 md:col-span-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            doc.doc_type === 'Rechnung' ? 'bg-blue-500/20 text-blue-500' :
                            doc.doc_type === 'Mahnung' ? 'bg-red-500/20 text-red-500' :
                            'bg-white/30 dark:bg-white/10 text-muted-foreground'
                          }`}>
                            <FileText className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate text-foreground">
                              {doc.sender_name || doc.original_name || 'Unbekannt'}
                            </p>
                            {doc.invoice_no && (
                              <p className="text-xs text-muted-foreground font-mono">
                                {doc.invoice_no}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Type */}
                      <div className="hidden md:block col-span-2">
                        <span className="text-sm text-muted-foreground">{doc.doc_type}</span>
                      </div>

                      {/* Amount */}
                      <div className="hidden md:block col-span-2">
                        <span className="font-medium tabular-nums text-foreground">{formatCurrency(doc.amount)}</span>
                      </div>

                      {/* Date */}
                      <div className="hidden md:block col-span-2">
                        <span className="text-sm text-muted-foreground">
                          {doc.created_at ? format(new Date(doc.created_at), 'd. MMM yyyy', { locale: de }) : '—'}
                        </span>
                      </div>

                      {/* Status */}
                      <div className="hidden md:block col-span-2">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          doc.status === 'done' ? 'bg-emerald-500/20 text-emerald-500' :
                          doc.status === 'urgent' ? 'bg-red-500/20 text-red-500' :
                          doc.status === 'progress' ? 'bg-blue-500/20 text-blue-500' :
                          'bg-amber-500/20 text-amber-500'
                        }`}>
                          {doc.status === 'done' ? 'Erledigt' :
                           doc.status === 'urgent' ? 'Dringend' :
                           doc.status === 'progress' ? 'In Arbeit' : 'Pipeline'}
                        </span>
                      </div>

                      {/* Mobile: Additional Info */}
                      <div className="col-span-12 md:hidden flex items-center justify-between text-sm text-muted-foreground">
                        <span>{doc.doc_type}</span>
                        <span className="font-medium text-foreground tabular-nums">{formatCurrency(doc.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </main>
      </div>

      {/* Document Drawer */}
      <DocumentDrawer
        document={selectedDoc}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedDoc(null); }}
        onUpdate={loadDocuments}
      />
    </div>
  );
};

export default ArchivePage;
